import { CommonFormDialog } from "@/components/common/CommonFormDialog";
import { CommonPagination } from "@/components/common/CommonPagination";
import { ConfirmationDialog } from "@/components/common/ConfirmationDialog";
import Loading from "@/components/common/Loading";
import CreateAdminCurrencyForm from "@/components/forms/CreateAdminCurrencyForm";
import UpdateAdminCurrencyForm from "@/components/forms/UpdateAdminCurrencyForm";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { QUERY_KEY } from "@/constant/queryKey";
import { useDeleteAdminCurrency } from "@/hooks/services/use-currency";
import { useDebounce } from "@/hooks/use-debounce";
import { usePagination } from "@/hooks/use-pagination";
import { useUrlParams } from "@/hooks/use-url-params";
import {
  ADMIN_CURRENCY_ENDPOINT,
  AdminCurrency,
} from "@/services/core/admin-currency.api";
import { useDialogStore } from "@/store/dialog-store";
import { format } from "date-fns";
import { Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const CurrencyPage = () => {
  const { params, updateParams } = useUrlParams();
  const [searchInput, setSearchInput] = useState(params.q);
  const debouncedSearch = useDebounce(searchInput, 500);

  const {
    data: currency,
    isLoading,
    isError,
    meta,
    setPage,
    setSearch,
    setLimit,
  } = usePagination<AdminCurrency>({
    endpoint: ADMIN_CURRENCY_ENDPOINT,
    queryKey: [QUERY_KEY.ADMIN_CURRENCY],
    initialParams: {
      currentPage: params.currentPage,
      pageSize: params.pageSize,
      s: params.s,
      q: params.q,
    },
  });

  console.log("currency", currency);

  const [selectedCurrency, setSelectedCurrency] =
    useState<AdminCurrency | null>(null);

  const {
    createDialog,
    updateDialog,
    deleteDialog,
    openCreateDialog,
    closeCreateDialog,
    openUpdateDialog,
    closeUpdateDialog,
    openDeleteDialog,
    closeDeleteDialog,
  } = useDialogStore();

  const deleteMutation = useDeleteAdminCurrency();

  const handleAdminEdit = (currency: AdminCurrency) => {
    setSelectedCurrency(currency);
    openUpdateDialog();
  };

  const handleAdminDeleteSubmit = async () => {
    if (!selectedCurrency) return;
    try {
      await deleteMutation.mutateAsync(selectedCurrency.id);

      toast.success("Delete currency successful!");
    } catch (error) {
      console.error("Failed to create currency:", error);
    }
  };

  const handlePageChange = (page: number) => {
    updateParams({ currentPage: page });
    setPage(page);
  };

  const handleLimitChange = (limit: number) => {
    updateParams({ pageSize: limit, currentPage: 1 });
    setLimit(limit);
  };

  const handleSortChange = (sort: string) => {
    updateParams({ s: sort, currentPage: 1 });
  };

  const handleSearch = (value: string) => {
    const trimmedValue = value.trim();
    setSearchInput(value);

    if (trimmedValue !== params.q) {
      updateParams({ q: trimmedValue, currentPage: 1 });
    }
  };

  useEffect(() => {
    updateParams({ q: debouncedSearch, currentPage: 1 });
    setSearch(debouncedSearch || "");
    setPage(1);
  }, [debouncedSearch]);

  return (
    <div className="w-full space-y-4">
      <div className="flex w-full items-center justify-between gap-4 sm:flex-row sm:items-center">
        <div className="relative w-1/2">
          <Search className="text-muted-foreground absolute top-2.5 left-2.5 h-4 w-4" />
          <Input
            type="text"
            placeholder="Search currency..."
            className="pl-8"
            value={searchInput}
            onChange={(e) => handleSearch(e.target.value)}
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput("")}
              className="text-muted-foreground hover:text-foreground absolute top-2.5 right-2 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none"
            >
              <X className="h-4 w-4 cursor-pointer" />
              <span className="sr-only">Clear search</span>
            </button>
          )}
        </div>

        <div className="flex w-full items-center justify-end gap-2">
          <Button onClick={openCreateDialog} className="cursor-pointer">
            <Plus className="mr-2 h-4 w-4" /> Create Currency
          </Button>

          <Select defaultValue={params.s} onValueChange={handleSortChange}>
            <SelectTrigger className="w-42">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tableId:asc">ID (Asc)</SelectItem>
              <SelectItem value="tableId:desc">ID (Desc)</SelectItem>
              <SelectItem value="name:asc">Name (A-Z)</SelectItem>
              <SelectItem value="name:desc">Name (Z-A)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-full items-center justify-center">
          <Loading />
        </div>
      ) : isError ? (
        <div className="py-8 text-center text-red-500">
          Error loading payment channels
        </div>
      ) : (
        <>
          {currency.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Currency Code</TableHead>
                    <TableHead>Display Name</TableHead>
                    <TableHead>Created By</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currency.map((curr, idx) => {
                    const tableId =
                      (meta.currentPage - 1) * meta.pageSize + idx + 1;
                    return (
                      <TableRow key={curr.id}>
                        <TableCell className="font-medium">{tableId}</TableCell>
                        <TableCell className="uppercase">
                          {curr.currencyCode}
                        </TableCell>
                        <TableCell>{curr.displayName}</TableCell>
                        <TableCell>{curr.createdBy || "System"}</TableCell>
                        <TableCell>
                          {curr.createdAt
                            ? format(
                                new Date(curr.createdAt),
                                "dd MMM yyyy HH:mm",
                              )
                            : "N/A"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleAdminEdit(curr)}
                              aria-label="Edit Currency"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="destructive"
                              size="icon"
                              onClick={() => {
                                setSelectedCurrency(curr);
                                openDeleteDialog();
                              }}
                              aria-label="Delete Currency"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex h-42 items-center justify-center">
              <p>There is no data</p>
            </div>
          )}

          {meta && (
            <CommonPagination
              page={meta.currentPage}
              totalPages={meta.totalPages}
              limit={meta.pageSize}
              hasNextPage={meta.hasNextPage}
              hasPrevPage={meta.hasPreviousPage}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
              limitOptions={[10, 25, 50, 100]}
              showLimitSelector={true}
            />
          )}
        </>
      )}

      {/* Create Dialog */}
      <CommonFormDialog
        title="Create new currency"
        description="Fill in the details to create a new currency."
        isOpen={createDialog}
        onOpenChange={closeCreateDialog}
      >
        <CreateAdminCurrencyForm />
      </CommonFormDialog>

      {/* Update Dialog */}
      <CommonFormDialog
        title="Update Currency"
        description="Update the details of this currency."
        isOpen={updateDialog}
        onOpenChange={closeUpdateDialog}
      >
        {selectedCurrency && (
          <UpdateAdminCurrencyForm
            selectedCurrency={selectedCurrency}
            setSelectedCurrency={setSelectedCurrency}
            initialData={{
              currencyCode: selectedCurrency.currencyCode,
              displayName: selectedCurrency.displayName,
            }}
          />
        )}
      </CommonFormDialog>

      {/* Delete Dialog */}
      <ConfirmationDialog
        open={deleteDialog}
        onOpenChange={closeDeleteDialog}
        onConfirm={handleAdminDeleteSubmit}
        confirmText={deleteMutation.isPending ? "Deleting..." : "Delete"}
        title="Deleting Currency?"
        description="This action cannot be undone. This will permanently delete your currency from our servers."
        loading={deleteMutation.isPending}
      />
    </div>
  );
};

export default CurrencyPage;
